import BaseController from "confinus_ui/base_controller";

export default class ProductImageGalleryController extends BaseController {
  public static targets = [
    "mainImage",
    "mainImageWrapper",
    "thumbnail",
    "lightbox",
    "lightboxImage",
    "lightboxCounter",
  ];

  public static values = {
    fullUrls: Array,
  };

  declare private readonly mainImageTarget: HTMLImageElement;
  declare private readonly mainImageWrapperTarget: HTMLElement;
  declare private readonly thumbnailTargets: HTMLElement[];
  declare private readonly lightboxTarget: HTMLDivElement;
  declare private readonly lightboxImageTarget: HTMLImageElement;
  declare private readonly hasLightboxCounterTarget: boolean;
  declare private readonly lightboxCounterTarget: HTMLElement;
  declare private readonly fullUrlsValue: string[];

  private currentIndex = 0;

  public disconnect() {
    document.body.style.overflow = "";
    super.disconnect();
  }

  public onMainImageLoad() {
    this.mainImageWrapperTarget.classList.remove("bg-gray-200");
  }

  public selectThumbnail(event: Event) {
    const button = (event.currentTarget as HTMLElement).closest(
      "[data-index]",
    ) as HTMLElement | null;
    if (!button) return;
    const index = parseInt(button.dataset.index || "0", 10);
    this.showImage(index);
  }

  public openLightbox() {
    this.openLightboxAtIndex(this.currentIndex);
  }

  public openLightboxAt(event: Event) {
    event.preventDefault();
    const button = (event.currentTarget as HTMLElement).closest(
      "[data-index]",
    ) as HTMLElement | null;
    if (!button) return;
    const index = parseInt(button.dataset.index || "0", 10);
    this.openLightboxAtIndex(index);
  }

  public closeLightbox(event?: Event) {
    if (this.lightboxTarget.classList.contains("hidden")) return;

    if (
      event &&
      event.type === "click" &&
      event.target !== this.lightboxTarget
    ) {
      const target = event.target as HTMLElement;
      if (
        !target.closest('[data-action*="closeLightbox"], [aria-label="Close"]')
      ) {
        return;
      }
    }
    this.lightboxTarget.classList.add("hidden");
    this.lightboxTarget.classList.remove("flex");
    document.body.style.overflow = "";
  }

  public lightboxPrev(event: Event) {
    event.stopPropagation();
    const count = this.fullUrlsValue.length;
    if (count <= 1) return;
    const newIndex = (this.currentIndex - 1 + count) % count;
    this.showLightboxImage(newIndex);
  }

  public lightboxNext(event: Event) {
    event.stopPropagation();
    const count = this.fullUrlsValue.length;
    if (count <= 1) return;
    const newIndex = (this.currentIndex + 1) % count;
    this.showLightboxImage(newIndex);
  }

  public stopPropagation(event: Event) {
    event.stopPropagation();
  }

  // --- Private ---

  private showImage(index: number) {
    if (this.thumbnailTargets.length === 0) return;
    index = Math.max(0, Math.min(index, this.thumbnailTargets.length - 1));
    this.currentIndex = index;

    const thumb = this.thumbnailTargets[index];
    if (thumb) {
      const img = thumb.querySelector("img") as HTMLImageElement;
      if (img) {
        this.mainImageTarget.src = img.src;
      }
    }

    this.thumbnailTargets.forEach((t, i) => {
      if (i === index) {
        t.classList.remove("border-transparent", "hover:border-gray-300");
        t.classList.add("border-primary-600");
      } else {
        t.classList.remove("border-primary-600");
        t.classList.add("border-transparent", "hover:border-gray-300");
      }
    });
  }

  private openLightboxAtIndex(index: number) {
    const urls = this.fullUrlsValue;
    if (!urls || urls.length === 0) return;
    index = Math.max(0, Math.min(index, urls.length - 1));
    this.showLightboxImage(index);
    this.lightboxTarget.classList.remove("hidden");
    this.lightboxTarget.classList.add("flex");
    document.body.style.overflow = "hidden";
  }

  private showLightboxImage(index: number) {
    this.currentIndex = index;
    const urls = this.fullUrlsValue;
    if (urls[index]) {
      this.lightboxImageTarget.src = urls[index];
    }
    if (this.hasLightboxCounterTarget && urls.length > 1) {
      this.lightboxCounterTarget.textContent = `${index + 1} / ${urls.length}`;
    }
    if (this.thumbnailTargets.length > 0) {
      this.showImage(index);
    }
  }
}
